import React, { useState } from "react";
import { Checkbox, Chip, Stack, Tooltip, Typography } from "@mui/material";
import { connect } from "react-redux";

import {
  getCustomerData,
  getCustomerDataAddresses,
  getCustomerDataContacts,
} from "../../redux/customerData";
import { useIndustryListWithCache } from "../../components/IndustryHookFormSelect/useIndustryListWithCache";
import {
  StyledSideBar,
  StyledTitleStack,
  StyledAvatar,
  StyledContactButton,
  StyledAddressStack,
  StyledAddressCard,
  StyledSectionDivider,
  StyledDetailStack,
  StyledFormControlLabel,
  StyledEditButton,
} from "./styles";

import { InputLabel } from "../../components";
import ContactDrawer from "./ContactDrawer";
import { RANKS } from "../../constants/customers";

const sharedProps = {
  variant: "contained",
  size: "small",
};

const getAddressType = (address) => {
  return address.billing && address.shipping
    ? "Main"
    : address.billing
    ? "Billing"
    : address.shipping
    ? "Shipping"
    : "";
};

const NonEditInput = ({ label, value, children }) => (
  <Stack>
    <Typography variant="inputLabelSmall" sx={{ color: "hubColors.mainFocus" }}>
      {label}
    </Typography>
    {value && <Typography variant="caption">{value}</Typography>}
    {children}
  </Stack>
);

const ContactSideBar = ({
  isVendor = false,
  details = {},
  addresses = [],
  contacts = [],
}) => {
  const [openEdit, setOpenEdit] = useState(false);
  const { industries } = useIndustryListWithCache();

  const primary =
    contacts.length &&
    (contacts?.find((c) => c?.isPrimary) ||
      contacts?.find((c) => !!c?.email) ||
      contacts[0]);

  const filteredAddress = addresses?.filter((a) => a?.id && !a?.deleted);
  const ranking = RANKS?.find(({ id }) => id === details?.ranking) || RANKS[0];

  const industryName = industries.find(industry => industry.id === details.industry)?.label || "Not Set";

  const initials = details.customer
    ? details.customer
        .split(" ")
        .slice(0, 2)
        .map((w) => w[0])
        .join("")
        .toUpperCase()
    : "?";

  return (
    <StyledSideBar>
      <StyledTitleStack>
        <StyledAvatar>{initials}</StyledAvatar>
        <Stack alignItems="center" gap="4px">
          <Typography variant="h3">{details.customer}</Typography>
          {!!primary && (
            <Stack alignItems="center" gap="2px">
              <Typography variant="body2" sx={{ color: "hubColors.greyShade" }}>
                {primary?.name}
              </Typography>
              <Typography variant="caption" sx={{ color: "hubColors.greyShade" }}>
                {primary?.email}
              </Typography>
            </Stack>
          )}
        </Stack>
        <Chip
          label={details.active ? "Active" : "Inactive"}
          size="small"
          sx={{
            fontSize: 11,
            fontWeight: 600,
            backgroundColor: details.active
              ? "hubColors.greenMainTransparent"
              : "hubColors.redMainTransparent",
            color: details.active ? "hubColors.greenMain" : "hubColors.redMain",
          }}
        />
        <Stack direction="row" gap="12px">
          <Tooltip title="Call">
            <span>
              <StyledContactButton
                icon="Phone"
                {...sharedProps}
                disabled={!(primary?.phoneNumber || primary?.workNumber)}
              />
            </span>
          </Tooltip>
          <Tooltip title="Message">
            <span>
              <StyledContactButton
                icon="MessageSquare"
                {...sharedProps}
                disabled={!(primary?.phoneNumber || primary?.workNumber)}
              />
            </span>
          </Tooltip>
          <Tooltip title="Email">
            <span>
              <StyledContactButton
                icon="Mail"
                {...sharedProps}
                disabled={!primary?.email}
              />
            </span>
          </Tooltip>
        </Stack>
      </StyledTitleStack>
      <StyledAddressStack>
        <Stack direction="row" justifyContent="flex-end">
          <StyledEditButton
            label="Edit Details"
            variant="contained"
            size="small"
            color="grey"
            onClick={() => setOpenEdit(true)}
          />
        </Stack>
        <StyledAddressCard>
          <StyledDetailStack>
            <NonEditInput label="Industry" value={industryName} />
            <NonEditInput label="Vendor" value={isVendor ? "Yes" : "No"} />
            <NonEditInput label="Ranking" value={ranking?.label} />
          </StyledDetailStack>
          {details?.notes && (
            <NonEditInput label="Notes" value={details.notes} />
          )}
          {!!details?.locations?.length && (
            <NonEditInput label="Locations">
              <Stack direction="row" flexWrap="wrap" gap="4px" mt="2px">
                {details.locations.map((l) => (
                  <Chip
                    key={l?.id}
                    label={l?.name}
                    size="small"
                    sx={{ fontSize: 10 }}
                  />
                ))}
              </Stack>
            </NonEditInput>
          )}
        </StyledAddressCard>
        {!!filteredAddress?.length && (
          <StyledSectionDivider>
            <Typography
              variant="inputLabelSmall"
              sx={{ color: "hubColors.greyShade", whiteSpace: "nowrap" }}
            >
              Addresses
            </Typography>
            <hr />
          </StyledSectionDivider>
        )}
        {filteredAddress?.map((address, i) => (
          <StyledAddressCard key={address.id ?? i}>
            <Typography variant="boldCaption" sx={{ color: "hubColors.mainFocus" }}>
              {getAddressType(address)} Address
            </Typography>
            <StyledDetailStack>
              <NonEditInput label="Address" value={address.street} />
              <NonEditInput label="City" value={address.city} />
              {address.unit && (
                <>
                  <NonEditInput label="Address Line" value={address.unit} />
                  <div />
                </>
              )}
              <NonEditInput label="State" value={address.state.toUpperCase()} />
              <NonEditInput label="Zip Code" value={address.zip} />
              <NonEditInput label="Country" value={address.country} />
              <div />
              <StyledFormControlLabel
                control={<Checkbox checked={address.billing} disabled />}
                labelPlacement="end"
                label={<InputLabel label="Billing" smallLabel />}
              />
              <StyledFormControlLabel
                control={<Checkbox checked={address.shipping} disabled />}
                labelPlacement="end"
                label={<InputLabel label="Shipping" smallLabel />}
              />
            </StyledDetailStack>
          </StyledAddressCard>
        ))}
      </StyledAddressStack>
      <ContactDrawer
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        isVendor={isVendor}
      />
    </StyledSideBar>
  );
};

const mapStateToProps = (state) => ({
  details: getCustomerData(state),
  addresses: getCustomerDataAddresses(state),
  contacts: getCustomerDataContacts(state),
});

export default connect(mapStateToProps)(ContactSideBar);
